"use client";

import { useState } from "react";
import Image from "next/image";
import Logo from "../../../public/images/logo_blue.png";
import Link from "next/link";
import FacebookIcon from "../icons/FacebookIcon";
import LinkedinIcon from "../icons/LinkedinIcon";
import LoadingButtonAnimation from "../ui/LoadingButtonAnimation";
import apiConfig from "../../lib/config";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");
  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch(`${apiConfig.api.baseUrl}/add_to_newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      setMessage("Thanks for subscribing!");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    }
  };

  return (
    <footer className="bg-[#0A2540] text-white pt-[120px] px-5 pb-[60px] ">
      <div className="max-w-[1440px] mx-auto flex gap-16 md:gap-[100px] flex-col md:flex-row items-center">
        {/* Left Section: Logo + Description */}
        <div className="flex flex-col items-start">
          <Image src={Logo} alt="logo" className="h-10 w-[232px] mb-5 " />
          <p className="text-[16px] text-white max-w-[340px] leading-6 font-openSans">
            A product of Drivestamp, Empowering Your Fleet business. Maintain
            health and condition with maximum transparency.
          </p>
        </div>

        <div className="flex gap-10 w-full flex-col md:flex-row">
          {/* Center Section: Newsletter */}
          <div className="max-w-[700px] w-full ">
            <h3 className="text-lg font-bold font-openSans mb-2.5">
              Join Our Newsletter
            </h3>
            <form className="flex w-full gap-2.5" onSubmit={handleSubscribe}>
              <div className="w-full">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full p-3  border border-[#DFDFDF] rounded-md text-white focus:outline-none focus:border-cyan-500 transition"
                />
                {message && (
                  <p
                    className={`mt-2 text-sm ${
                      status === "error" ? "text-red-400" : "text-green-400"
                    }`}
                  >
                    {message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="submit-button max-w-27 w-full h-13"
                //   className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold rounded-r-lg transition duration-200"
              >
                {status === "loading" ? (
                  <LoadingButtonAnimation />
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>
          </div>

          {/* Right Section: Stay Up to Date */}
          <div className="">
            <h4 className="text-[22px] font-bold mb-2.5 font-openSans">
              Stay up to date
            </h4>
            <p className="text-sm text-[#F7F7F7] max-w-[332px] leading-5 font-openSans">
              Subscribe to our newsletter and get connected with us to gain
              deeper business insights and future updates. You can unsubscribe
              at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-[1440px] mx-auto mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between  items-center text-sm text-gray-500">
        <div className="flex gap-5 flex-col md:flex-row items-center">
          <div className="flex space-x-2.5 mb-4 md:mb-0">
            <Link href="#" className="hover:text-cyan-400 transition">
              <FacebookIcon />
            </Link>
            <Link href="#" className="hover:text-cyan-400 transition">
              <LinkedinIcon />
            </Link>
          </div>

          <div className="flex space-x-1.5 md:space-x-5 mb-4 md:mb-0 text-[14px] leading-5 text-white">
            <span className="text-[#7D7D7D] hidden md:block">|</span>
            <Link
              href="/terms-of-service"
              className="hover:text-cyan-400 transition"
            >
              Terms of service
            </Link>
            <span className="text-[#7D7D7D]">|</span>
            <Link
              href="/privacy-policy"
              className="hover:text-cyan-400 transition"
            >
              Privacy
            </Link>
            <span className="text-[#7D7D7D]">|</span>
            <Link
              href="/cookie-policy"
              className="hover:text-cyan-400 transition"
            >
              Cookies
            </Link>
            <span className="text-[#7D7D7D]">|</span>
            <Link
              href="/acceptable-use-policy"
              className="hover:text-cyan-400 transition"
            >
              Acceptable policy
            </Link>
          </div>
        </div>
        <div className="text-white text-[16px] leading-6">
          © {new Date().getFullYear()} Drivestamp. All Rights Reserved
        </div>
      </div>
    </footer>
  );
};

export default Footer;
